/**
 * Account selector modal: one fixed rectangular size.
 * Step 1: Grid of square broker tiles (icon above headline, centered).
 * Step 2: Rectangular account cards, one per row (label, last sync/import, actions).
 */

// @ts-nocheck Chakra v1 ModalContent responsive props: IDE TS union depth with fragment sibling.
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { toastError, presentMutationError } from "../../utils/showApiErrorToast";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  SimpleGrid,
  Box,
  Text,
  useColorModeValue,
  Spinner,
  Flex,
  HStack,
  Icon,
  VStack,
  IconButton,
  Input,
  Button,
  Image,
  Badge,
} from "@chakra-ui/react";
import { toast } from "sonner";
import { MdAccountBalance, MdCreditCard, MdDeleteOutline, MdSync, MdUpload } from "react-icons/md";
import liquidBrokersLogo from "../../assets/img/liquid brokers logo.jpeg";
import type { TradeAccountOption } from "../../contexts/ActiveBrokerAccountContext";
import {
  deleteBrokerAccount,
  fetchBrokerCapabilities,
  syncBrokerTrades,
  updateBrokerAccountCustomName,
} from "../../services/brokerIntegrationApi";
import { summarizeRemoveAccountResult } from "../../utils/brokerAccountRemoval";
import { afterBrokerResyncWaitForAnalytics } from "../../utils/brokerSyncStatusPoll";
import { RemoveAccountAlertDialog } from "./RemoveAccountAlertDialog";
import GradientButton from "../../views/Dashboard/Profile/components/GradientButton";
import { useHistory } from "react-router-dom";
import {
  isTradeAccountBrokerBackedForUi,
  canResyncTradeAccountForUi,
  isTradeAccountCsvOnlyForUi,
  resolveTradeAccountSourceForUi,
  filterConnectedTradeAccountsForUi,
} from "../../utils/tradeAccountSource";

import { useRunInstrumentSetupAfterSync } from "../../hooks/useRunInstrumentSetupAfterSync";
import {
  buildProfileBrokerApiUrl,
  normalizeProfileBrokerSlug as normalizeBrokerSlug,
  resolveProfileBrokerTabForImport,
} from "../../utils/profileBrokerNavigation";

/** Format ISO timestamp from GET /broker/trade-accounts for the account picker. */
function formatAccountActivityTs(iso: string | null | undefined): string | null {
  if (iso == null || String(iso).trim() === "") return null;
  const d = new Date(String(iso).trim());
  if (Number.isNaN(d.getTime())) return null;
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(d);
  } catch {
    return String(iso);
  }
}

const EditIcon = (props: { w?: number; h?: number }) => (
  <Box
    as="svg"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth="1.5"
    stroke="currentColor"
    w={props.w ?? 4}
    h={props.h ?? 4}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
    />
  </Box>
);

const MODAL_WIDTH = "520px";
const GRID_COLUMNS = 4;
const GRID_GAP = 3;
const TILE_MIN_SIZE = "100px";
const GRID_MIN_HEIGHT = "320px";
const BROKER_GRID_COLUMNS = 3;
const BROKER_GRID_MAX_HEIGHT = "320px";

export interface AccountSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  accounts: TradeAccountOption[];
  activeAccountCode: string | null;
  activeBrokerName: string | null;
  onSelectAccount: (brokerName: string, accountCode: string) => Promise<void>;
  /** Call after saving custom name so accounts list is refreshed from API. */
  onRefetchAccounts?: () => Promise<void | string | null>;
  /** Drop removed account from picker immediately (before refetch). */
  onAccountRemoved?: (brokerName: string, accountCode: string) => Promise<void>;
  loading?: boolean;
  isSwitching?: boolean;
  isConnected: boolean;
}

function rowKey(a: Pick<TradeAccountOption, "brokerName" | "accountCode">): string {
  return `${a.brokerName}:${a.accountCode}`;
}

/** Pill from resolved `source` (see `resolveTradeAccountSourceForUi` in `tradeAccountSource.ts`). */
function accountSourceBadgeInfo(acc: TradeAccountOption): { label: string; bg: string } | null {
  const s = resolveTradeAccountSourceForUi(acc);
  if (!s) return null;
  if (s === "imported_trades" || s === "csv_import") {
    return { label: "CSV import", bg: "orange.400" };
  }
  if (s === "credentials" || s === "broker_list" || s === "synced_trades") {
    return { label: "Broker sync", bg: "cyan.500" };
  }
  return {
    label: s.replace(/_/g, " "),
    bg: "gray.600",
  };
}

export function AccountSelectorModal({
  isOpen,
  onClose,
  accounts,
  activeAccountCode,
  activeBrokerName,
  onSelectAccount,
  onRefetchAccounts,
  onAccountRemoved,
  loading = false,
  isSwitching = false,
  isConnected,
}: AccountSelectorModalProps) {
  const [step, setStep] = useState<"brokers" | "accounts">("brokers");
  const [selectedBrokerId, setSelectedBrokerId] = useState<string | null>(null);
  const [editingAccountCode, setEditingAccountCode] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState("");
  const [savingCustomName, setSavingCustomName] = useState(false);
  /** Optimistic display name per account – name updates instantly on Save while request runs. */
  const [optimisticCustomNames, setOptimisticCustomNames] = useState<Record<string, string>>({});
  /** Kept in sync on every keystroke so Save always has the current value. */
  const latestValueRef = useRef("");
  const [deleteTarget, setDeleteTarget] = useState<TradeAccountOption | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const deleteCancelRef = useRef<HTMLButtonElement>(null);
  const deleteConfirmInputRef = useRef<HTMLInputElement>(null);
  const [brokerAutoSyncBySlug, setBrokerAutoSyncBySlug] = useState<Record<string, boolean>>({});
  const [syncingRowKey, setSyncingRowKey] = useState<string | null>(null);
  const history = useHistory();
  const runInstrumentSetupAfterSync = useRunInstrumentSetupAfterSync();

  const borderColor = useColorModeValue("gray.200", "gray.600");
  const tileBg = useColorModeValue("white", "whiteAlpha.100");
  const tileHoverBg = useColorModeValue("gray.50", "whiteAlpha.200");
  const tileActiveBorder = useColorModeValue("cyan.400", "cyan.500");
  const mutedColor = useColorModeValue("gray.500", "gray.400");
  const labelColor = useColorModeValue("gray.600", "gray.400");
  const accountIconInactiveColor = "white";

  const connectedAccounts = useMemo(
    () => filterConnectedTradeAccountsForUi(accounts),
    [accounts]
  );

  const connectedBrokers = useMemo(() => {
    if (!isConnected || connectedAccounts.length === 0) return [];
    const m = new Map<string, string>();
    connectedAccounts.forEach((a) => {
      if (!m.has(a.brokerName)) {
        m.set(a.brokerName, a.brokerDisplayName || a.brokerName);
      }
    });
    return Array.from(m.entries()).map(([id, name]) => ({ id, name }));
  }, [isConnected, connectedAccounts]);

  const visibleAccounts = useMemo(() => {
    if (!selectedBrokerId) return connectedAccounts;
    return connectedAccounts.filter((a) => a.brokerName === selectedBrokerId);
  }, [connectedAccounts, selectedBrokerId]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    void (async () => {
      try {
        const caps = await fetchBrokerCapabilities();
        if (cancelled || !Array.isArray(caps)) return;
        const next: Record<string, boolean> = {};
        caps.forEach((c: { broker_name?: string; auth?: { supports_auto_sync?: boolean } }) => {
          const slug = normalizeBrokerSlug(String(c.broker_name || ""));
          next[slug] = !!c.auth?.supports_auto_sync;
        });
        setBrokerAutoSyncBySlug(next);
      } catch {
        if (!cancelled) setBrokerAutoSyncBySlug({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setEditingAccountCode(null);
      setOptimisticCustomNames({});
      setSyncingRowKey(null);
      if (connectedBrokers.length === 1) {
        setSelectedBrokerId(connectedBrokers[0].id);
        setStep("accounts");
      } else {
        setSelectedBrokerId(null);
        setStep("brokers");
      }
    }
  }, [isOpen, connectedBrokers]);

  const saveCustomName = useCallback(
    async (accountCode: string, brokerName: string, compositeKey: string, value: string) => {
      const trimmed = value.trim();
      const displayName = trimmed || "";
      setOptimisticCustomNames((prev) => ({ ...prev, [compositeKey]: displayName }));
      setEditingAccountCode(null);
      setEditingValue("");
      setSavingCustomName(true);
      try {
        await updateBrokerAccountCustomName(brokerName, accountCode, trimmed || null);
        await onRefetchAccounts?.();
        window.dispatchEvent(new CustomEvent("accountCustomNameChanged"));
        setOptimisticCustomNames((prev) => {
          const next = { ...prev };
          delete next[compositeKey];
          return next;
        });
        toast.success("Name saved", {
          description: trimmed ? `Display name set to "${trimmed}".` : "Custom name cleared.",
        });
      } catch (err) {
        console.error("Failed to save custom name:", err);
        setOptimisticCustomNames((prev) => {
          const next = { ...prev };
          delete next[compositeKey];
          return next;
        });
        const message = err instanceof Error ? err.message : "Failed to save custom name.";
        toastError("Could not save name", {
          description: message,
        });
      } finally {
        setSavingCustomName(false);
      }
    },
    [onRefetchAccounts]
  );

  const startEditing = useCallback((e: React.MouseEvent, account: TradeAccountOption) => {
    e.stopPropagation();
    const initial = account.customName ?? "";
    latestValueRef.current = initial;
    setEditingAccountCode(rowKey(account));
    setEditingValue(initial);
  }, []);

  const handleSelectAccount = async (acc: TradeAccountOption) => {
    if (
      isSwitching ||
      (acc.accountCode === activeAccountCode && acc.brokerName === activeBrokerName)
    ) {
      return;
    }
    try {
      await onSelectAccount(acc.brokerName, acc.accountCode);
      onClose();
    } catch {
      // caller handles toast
    }
  };

  const handleBack = () => {
    setStep("brokers");
    if (connectedBrokers.length > 1) {
      setSelectedBrokerId(null);
    }
  };

  const handleSyncBrokerAccount = useCallback(
    async (e: React.MouseEvent, acc: TradeAccountOption) => {
      e.preventDefault();
      e.stopPropagation();
      const rk = rowKey(acc);
      setSyncingRowKey(rk);
      try {
        const result = await syncBrokerTrades(normalizeBrokerSlug(acc.brokerName), acc.accountCode);
        const st = result?.sync_stats;
        const parts =
          st && typeof st === "object"
            ? [
                typeof st.created === "number" ? `${st.created} new` : "",
                typeof st.updated === "number" ? `${st.updated} updated` : "",
                typeof st.skipped === "number" ? `${st.skipped} skipped` : "",
              ]
                .filter(Boolean)
                .join(", ")
            : "";
        const syncStatus = await afterBrokerResyncWaitForAnalytics({
          accountCode: acc.accountCode,
          brokerName: acc.brokerName,
          action: "brokerSync",
          refetchBrokerAccounts: onRefetchAccounts,
        });
        if (syncStatus.phase === "needs_instrument_setup") {
          toast.info("Instrument setup required", {
            description:
              "Sync completed. Resolve pending symbols before dashboard analytics can load.",
          });
          await runInstrumentSetupAfterSync(
            syncStatus,
            syncStatus.account_code || acc.accountCode,
            { skipTradeAccountsRefetch: true }
          );
          return;
        }
        await runInstrumentSetupAfterSync(result, acc.accountCode, {
          skipTradeAccountsRefetch: true,
        });
        toast.success("Sync completed", {
          description:
            parts ||
            `Trades were synced (${syncStatus.closed_trades} closed, ${syncStatus.open_trades} open).`,
        });
      } catch (err) {
        presentMutationError(err, "Sync failed");
      } finally {
        setSyncingRowKey(null);
      }
    },
    [onRefetchAccounts, runInstrumentSetupAfterSync]
  );

  const closeDeleteDialog = useCallback(() => {
    setDeleteTarget(null);
    setDeleteConfirmText("");
  }, []);

  const openDeleteDialog = useCallback((e: React.MouseEvent, acc: TradeAccountOption) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteConfirmText("");
    setDeleteTarget(acc);
  }, []);

  const confirmRemoveAccount = useCallback(async () => {
    if (!deleteTarget) return;
    if (deleteConfirmText !== deleteTarget.accountCode) return;
    setIsDeletingAccount(true);
    try {
      const result = await deleteBrokerAccount({
        broker_account_code: deleteTarget.accountCode,
        broker_name: deleteTarget.brokerName,
      });
      clearAllTradeCaches();
      window.dispatchEvent(
        new CustomEvent("tradesUpdated", { detail: { action: "removeBrokerAccount" } })
      );
      await onAccountRemoved?.(deleteTarget.brokerName, deleteTarget.accountCode);
      await onRefetchAccounts?.();
      toast.success("Account data removed", {
        description: summarizeRemoveAccountResult(result),
      });
      closeDeleteDialog();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not remove account.";
      toastError("Remove failed", {
        description: message,
      });
    } finally {
      setIsDeletingAccount(false);
    }
  }, [deleteTarget, deleteConfirmText, onAccountRemoved, onRefetchAccounts, closeDeleteDialog]);

  useEffect(() => {
    if (!deleteTarget) return undefined;
    const id = window.setTimeout(() => {
      deleteConfirmInputRef.current?.focus();
    }, 50);
    return () => window.clearTimeout(id);
  }, [deleteTarget]);

  const deleteTargetIsActive =
    !!deleteTarget &&
    deleteTarget.accountCode === activeAccountCode &&
    deleteTarget.brokerName === activeBrokerName;
  const deleteConfirmMatches = !!deleteTarget && deleteConfirmText === deleteTarget.accountCode;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} scrollBehavior="inside" isCentered>
        <ModalOverlay />
        <ModalContent
        w={{ base: "calc(100vw - 32px)", sm: MODAL_WIDTH }}
        maxW="calc(100vw - 32px)"
        minH={{ base: "380px", sm: "440px" }}
        maxH="90vh"
        overflow="hidden"
        display="flex"
        flexDirection="column"
        borderRadius="12px"
      >
        <ModalHeader fontFamily="'Urbanist', sans-serif" fontSize="18px" fontWeight="500" color="#38BBD1" pb={2} flexShrink={0} px={{ base: 4, sm: 6 }}>
          {step === "brokers" ? "Select broker" : "Select account"}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6} pt={0} px={{ base: 4, sm: 6 }} overflowY="auto" minH={0} flex="1">
          {step === "accounts" && connectedBrokers.length > 1 && (
            <Flex align="center" mb={4} cursor="pointer" onClick={handleBack} role="group" flexShrink={0}>
              <Text fontSize="sm" color={mutedColor} _groupHover={{ color: "cyan.500" }} fontFamily="'IBM Plex Sans', sans-serif" textTransform="uppercase" fontWeight="medium">
                Back to brokers
              </Text>
            </Flex>
          )}

          {loading && !savingCustomName ? (
            <Flex align="center" justify="center" minH={GRID_MIN_HEIGHT}>
              <Spinner size="lg" />
            </Flex>
          ) : step === "brokers" ? (
            <>
              {!isConnected ? (
                <Flex align="center" justify="center" minH={GRID_MIN_HEIGHT}>
                  <Text fontSize="sm" color={mutedColor} textAlign="center" fontFamily="'IBM Plex Sans', sans-serif" textTransform="uppercase" fontWeight="medium">
                    Connect a broker in Profile to see and switch accounts.
                  </Text>
                </Flex>
              ) : connectedBrokers.length === 0 ? (
                <Flex align="center" justify="center" minH={GRID_MIN_HEIGHT}>
                  <Text fontSize="sm" color={mutedColor} textAlign="center" fontFamily="'IBM Plex Sans', sans-serif" textTransform="uppercase" fontWeight="medium">
                    No brokers connected. Connect one in Profile.
                  </Text>
                </Flex>
              ) : (
                <Box minH={GRID_MIN_HEIGHT} overflowY="auto" maxH={BROKER_GRID_MAX_HEIGHT}>
                  <SimpleGrid
                    columns={BROKER_GRID_COLUMNS}
                    spacing={GRID_GAP}
                    sx={{
                      gridTemplateColumns: "repeat(3, 1fr)",
                      "& > *": { minW: 0 },
                    }}
                  >
                    {connectedBrokers.map((broker) => (
                      <Box
                        key={broker.id}
                        as="button"
                        type="button"
                        borderRadius="10px"
                        sx={{ aspectRatio: "1" }}
                        border="2px solid"
                        borderColor={borderColor}
                        bg={tileBg}
                        _hover={{ borderColor: tileActiveBorder }}
                        _active={{ transform: "scale(0.98)" }}
                        transition="all 0.15s"
                        onClick={() => {
                          setSelectedBrokerId(broker.id);
                          setStep("accounts");
                        }}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        p={broker.id === "liquidbrokers" ? 2 : 3}
                      >
                        {broker.id === "liquidbrokers" ? (
                          <Image
                            src={liquidBrokersLogo}
                            alt={broker.name}
                            maxW="95%"
                            maxH="95%"
                            objectFit="contain"
                            ignoreFallback
                          />
                        ) : (
                          <>
                            <Flex
                              w="44px"
                              h="44px"
                              borderRadius="10px"
                              bg="linear-gradient(135deg, #38BBD1, #3EEAEC)"
                              align="center"
                              justify="center"
                              mb={2}
                            >
                              <Icon as={MdAccountBalance} w={6} h={6} color="white" />
                            </Flex>
                            <Text
                              fontSize="sm"
                              color="gray.400"
                              fontWeight={500}
                              fontFamily="'IBM Plex Sans', sans-serif"
                              textAlign="center"
                              textTransform="uppercase"
                              noOfLines={1}
                              overflow="hidden"
                              w="100%"
                            >
                              {broker.name}
                            </Text>
                          </>
                        )}
                      </Box>
                    ))}
                  </SimpleGrid>
                </Box>
              )}
            </>
          ) : (
            <>
              {visibleAccounts.length === 0 ? (
                <Flex align="center" justify="center" minH={GRID_MIN_HEIGHT}>
                  <Text fontSize="sm" color={mutedColor} textAlign="center" fontFamily="'IBM Plex Sans', sans-serif" textTransform="uppercase" fontWeight="medium">
                    No accounts found. Sync your broker in Profile to load accounts.
                  </Text>
                </Flex>
              ) : (
                <VStack spacing={3} align="stretch" minH={GRID_MIN_HEIGHT}>
                  {visibleAccounts.map((acc) => {
                    const rk = rowKey(acc);
                    const isActive =
                      acc.accountCode === activeAccountCode &&
                      acc.brokerName === activeBrokerName;
                    const isEditingThis = editingAccountCode === rk;
                    const displayName =
                      optimisticCustomNames[rk] ?? acc.customName?.trim() ?? "";
                    const primaryLabel =
                      displayName || acc.tradeLabel || acc.accountCode;
                    const showSubtitle =
                      !!displayName || primaryLabel.trim() !== acc.accountCode;
                    const sourceBadge = accountSourceBadgeInfo(acc);
                    const isCsvImportedAccount = isTradeAccountCsvOnlyForUi(acc);
                    const showBrokerSyncButton = canResyncTradeAccountForUi(
                      acc,
                      brokerAutoSyncBySlug[normalizeBrokerSlug(acc.brokerName)] === true
                    );
                    const lastBrokerSyncLabel = formatAccountActivityTs(acc.lastBrokerSyncAt);
                    const lastCsvImportLabel = formatAccountActivityTs(acc.lastCsvImportAt);

                    return (
                      <Box
                        key={rk}
                        w="100%"
                        borderRadius="10px"
                        border="2px solid"
                        borderColor={isActive ? tileActiveBorder : borderColor}
                        bg={isActive ? tileHoverBg : tileBg}
                        _hover={{ borderColor: tileActiveBorder }}
                        transition="all 0.15s"
                        display="flex"
                        alignItems="center"
                        textAlign="left"
                        p={4}
                      >
                        {/* When editing: no select area – only icon, edit block, edit button. Save is the only way to save. */}
                        {isEditingThis ? (
                          <>
                            <Flex
                              w="44px"
                              h="44px"
                              borderRadius="10px"
                              bg="linear-gradient(135deg, #38BBD1, #3EEAEC)"
                              align="center"
                              justify="center"
                              flexShrink={0}
                              mr={4}
                            >
                              <Icon
                                as={MdCreditCard}
                                w={6}
                                h={6}
                                color={accountIconInactiveColor}
                              />
                            </Flex>
                            <Flex flex={1} direction="column" align="stretch" minW={0} gap={2}>
                              <Flex align="center" gap={2}>
                                <Input
                                  id={`custom-name-${rk}`}
                                  size="sm"
                                  value={editingValue}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    latestValueRef.current = v;
                                    setEditingValue(v);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Escape") {
                                      e.preventDefault();
                                      setEditingAccountCode(null);
                                      setEditingValue("");
                                    }
                                  }}
                                  placeholder="CUSTOM NAME"
                                  fontFamily="'Urbanist', sans-serif"
                                  fontSize="sm"
                                  fontWeight="500"
                                  flex={1}
                                  minW={0}
                                  autoFocus
                                  isDisabled={savingCustomName}
                                  borderColor="#38BBD1"
                                  _hover={{ borderColor: "#38BBD1" }}
                                  _focus={{
                                    borderColor: "#38BBD1",
                                    boxShadow: "0 0 0 1px #38BBD1",
                                  }}
                                />
                                <GradientButton
                                  type="button"
                                  showArrow={false}
                                  size="sm"
                                  px="12px"
                                  py="8px"
                                  h="32px"
                                  fontSize="xs"
                                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const value = latestValueRef.current;
                                    saveCustomName(acc.accountCode, acc.brokerName, rk, value);
                                  }}
                                  isDisabled={savingCustomName}
                                >
                                  Save
                                </GradientButton>
                                <Button
                                  type="button"
                                  size="sm"
                                  px="12px"
                                  py="8px"
                                  h="32px"
                                  fontSize="xs"
                                  fontFamily="'IBM Plex Sans', sans-serif"
                                  textTransform="uppercase"
                                  fontWeight="medium"
                                  bgGradient="linear-gradient(135deg, #E00101, #FE4244)"
                                  color="white"
                                  _hover={{ opacity: 0.9 }}
                                  _active={{ opacity: 0.85 }}
                                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setEditingAccountCode(null);
                                    setEditingValue("");
                                  }}
                                  isDisabled={savingCustomName}
                                >
                                  Cancel
                                </Button>
                              </Flex>
                            </Flex>
                            <IconButton
                              aria-label="Cancel edit"
                              icon={<EditIcon w={4} h={4} />}
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingAccountCode(null);
                                setEditingValue("");
                              }}
                              flexShrink={0}
                              color={mutedColor}
                              _hover={{ color: "cyan.500", bg: "transparent" }}
                            />
                          </>
                        ) : (
                          <>
                            <Flex
                              flex={1}
                              align="center"
                              minW={0}
                              cursor={isSwitching ? "not-allowed" : "pointer"}
                              opacity={isSwitching ? 0.7 : 1}
                              onClick={() => !isSwitching && handleSelectAccount(acc)}
                              _active={{ transform: "scale(0.99)" }}
                              transition="transform 0.15s"
                            >
                              <Flex
                                w="44px"
                                h="44px"
                                borderRadius="10px"
                                bg="linear-gradient(135deg, #38BBD1, #3EEAEC)"
                                align="center"
                                justify="center"
                                flexShrink={0}
                                mr={4}
                              >
                                <Icon
                                  as={MdCreditCard}
                                  w={6}
                                  h={6}
                                  color={accountIconInactiveColor}
                                />
                              </Flex>
                              <Flex flex={1} direction="column" align="stretch" minW={0}>
                                <Flex align="center" gap={2} flexWrap="wrap" minW={0}>
                                  <Text
                                    fontSize="18px"
                                    fontWeight="500"
                                    color="#38BBD1"
                                    fontFamily="'Urbanist', sans-serif"
                                    noOfLines={1}
                                    overflow="hidden"
                                    textOverflow="ellipsis"
                                  >
                                    {primaryLabel}
                                    {!displayName && acc.isDemo ? " (Demo)" : ""}
                                  </Text>
                                  {sourceBadge ? (
                                    <Badge
                                      fontSize="9px"
                                      fontFamily="'IBM Plex Sans', sans-serif"
                                      fontWeight="medium"
                                      textTransform="uppercase"
                                      px={2}
                                      py={0.5}
                                      borderRadius="6px"
                                      bg={sourceBadge.bg}
                                      color="white"
                                      flexShrink={0}
                                      maxW="100%"
                                      noOfLines={1}
                                    >
                                      {sourceBadge.label}
                                    </Badge>
                                  ) : null}
                                </Flex>
                                {showSubtitle && (
                                  <Text
                                    fontSize="14px"
                                    color={labelColor}
                                    fontFamily="'IBM Plex Sans', sans-serif"
                                    textTransform="uppercase"
                                    fontWeight="medium"
                                    mt={0.5}
                                    noOfLines={1}
                                    overflow="hidden"
                                    textOverflow="ellipsis"
                                  >
                                    {acc.accountCode}
                                    {acc.isDemo ? " (Demo)" : ""}
                                  </Text>
                                )}
                                <Flex mt={1} direction="column" gap={0.5} align="stretch">
                                  {!isCsvImportedAccount ? (
                                    <Text
                                      fontSize="12px"
                                      color={labelColor}
                                      fontFamily="'IBM Plex Sans', sans-serif"
                                      fontWeight="medium"
                                    >
                                      Last broker sync: {lastBrokerSyncLabel ?? "—"}
                                    </Text>
                                  ) : null}
                                  {!isTradeAccountBrokerBackedForUi(acc) ? (
                                    <Text
                                      fontSize="12px"
                                      color={labelColor}
                                      fontFamily="'IBM Plex Sans', sans-serif"
                                      fontWeight="medium"
                                    >
                                      Last CSV import: {lastCsvImportLabel ?? "—"}
                                    </Text>
                                  ) : null}
                                </Flex>
                              </Flex>
                            </Flex>
                            <HStack spacing={0} flexShrink={0}>
                              <IconButton
                                aria-label="Edit account display name"
                                icon={<EditIcon w={4} h={4} />}
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  startEditing(e, acc);
                                }}
                                color={mutedColor}
                                _hover={{ color: "cyan.500", bg: "transparent" }}
                              />
                              {showBrokerSyncButton ? (
                                <IconButton
                                  aria-label="Sync trades from broker for this account"
                                  icon={<Icon as={MdSync} w={5} h={5} />}
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => void handleSyncBrokerAccount(e, acc)}
                                  isLoading={syncingRowKey === rk}
                                  isDisabled={
                                    isSwitching ||
                                    isDeletingAccount ||
                                    savingCustomName ||
                                    (syncingRowKey != null && syncingRowKey !== rk)
                                  }
                                  color={mutedColor}
                                  _hover={{ color: "cyan.500", bg: "transparent" }}
                                />
                              ) : null}
                              {isCsvImportedAccount ? (
                                <IconButton
                                  aria-label="Import more trades from CSV for this account"
                                  icon={<Icon as={MdUpload} w={5} h={5} />}
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    history.push(
                                      buildProfileBrokerApiUrl({
                                        brokerName: acc.brokerName,
                                        accountCode: acc.accountCode,
                                        brokerTab: resolveProfileBrokerTabForImport({
                                          brokerSupportsApiSync:
                                            brokerAutoSyncBySlug[
                                              normalizeBrokerSlug(acc.brokerName)
                                            ] === true,
                                          activeAccount: acc,
                                        }),
                                      })
                                    );
                                    onClose();
                                  }}
                                  isDisabled={isSwitching || isDeletingAccount || savingCustomName}
                                  color={mutedColor}
                                  _hover={{ color: "cyan.500", bg: "transparent" }}
                                />
                              ) : null}
                              <IconButton
                                aria-label="Remove Tessera data for this account (credentials unchanged)"
                                icon={<Icon as={MdDeleteOutline} w={5} h={5} />}
                                size="sm"
                                variant="ghost"
                                onClick={(e) => openDeleteDialog(e, acc)}
                                isDisabled={isSwitching || isDeletingAccount || savingCustomName}
                                color={mutedColor}
                                _hover={{ color: "red.400", bg: "transparent" }}
                              />
                            </HStack>
                          </>
                        )}
                      </Box>
                    );
                  })}
                </VStack>
              )}
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>

      <RemoveAccountAlertDialog
        deleteTarget={deleteTarget}
        deleteConfirmText={deleteConfirmText}
        setDeleteConfirmText={setDeleteConfirmText}
        deleteConfirmMatches={deleteConfirmMatches}
        deleteTargetIsActive={deleteTargetIsActive}
        isDeletingAccount={isDeletingAccount}
        deleteCancelRef={deleteCancelRef}
        deleteConfirmInputRef={deleteConfirmInputRef}
        borderColor={borderColor}
        mutedColor={mutedColor}
        labelColor={labelColor}
        onCancel={closeDeleteDialog}
        onConfirm={confirmRemoveAccount}
      />
    </>
  );
}
